<template>
  <aside class="flex h-full flex-col bg-base-100 border-r border-base-300/60">
    <!-- Sidebar header -->


    <!-- Search -->
    <div class="shrink-0 px-3 py-3">
      <label class="input input-sm input-bordered flex items-center gap-2 w-full bg-base-200/50">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 opacity-40 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="t('menu.searchPlaceholder')"
          class="grow bg-transparent text-sm min-w-0"
        />
        <kbd v-if="!searchQuery" class="kbd kbd-xs hidden sm:inline">/</kbd>
      </label>
    </div>

    <!-- Navigation -->
    <nav class="flex-1 overflow-y-auto px-2 pb-2 scrollbar-thin">
      <div v-for="section in filteredSections" :key="section.id" class="mb-1">
        <button
          class="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-base-content/40 hover:text-base-content/60 transition-colors"
          @click="toggleSection(section.id)"
        >
          <span>{{ section.title }}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-3.5 w-3.5 transition-transform duration-200"
            :class="{ 'rotate-180': !collapsedSections[section.id] }"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <ul v-show="!collapsedSections[section.id]" class="space-y-0.5 mt-0.5">
          <li v-for="item in section.items" :key="item.to">
            <router-link
              :to="item.to"
              class="group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-all"
              :class="isActive(item.to)
                ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary pl-2'
                : 'text-base-content/70 hover:bg-base-200 hover:text-base-content border-l-2 border-transparent pl-2'"
            >
              <span v-html="item.icon" class="shrink-0 opacity-70 group-hover:opacity-100" :class="{ 'opacity-100': isActive(item.to) }"></span>
              <span class="truncate">{{ item.label }}</span>
              <span v-if="item.badge" class="badge badge-primary badge-xs ml-auto">{{ item.badge }}</span>
            </router-link>
          </li>
        </ul>
      </div>

      <p v-if="filteredSections.length === 0" class="text-center text-sm text-base-content/40 py-6">
        {{ t('menu.noMatch') }}
      </p>
    </nav>

    <!-- Bottom -->
    <div class="shrink-0 border-t border-base-300/60 p-3 space-y-2">
      <button
        type="button"
        class="btn btn-ghost btn-sm w-full justify-start gap-2"
        @click="openContactModal"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
        </svg>
        {{ t('menu.helpSupport') }}
      </button>
    </div>
  </aside>
</template>

<script setup>
import { ref, computed, reactive, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import useLocale from "../../compositions/useLocale";
import useContactModal from "../../compositions/useContactModal";

const { t } = useLocale();
const { openContactModal } = useContactModal();
const route = useRoute();
const searchQuery = ref('');
const collapsedSections = reactive({
  main: false,
  management: false,
  other: false,
});

const iconHome = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>`;
const iconTasks = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" /></svg>`;
const iconCalendar = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 9v7.5" /></svg>`;
const iconSchema = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" /></svg>`;
const iconContacts = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" /></svg>`;
const iconError = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>`;

const sections = computed(() => [
  {
    id: 'main',
    title: t('menu.sections.main'),
    items: [
      { to: '/', label: t('menu.items.home'), icon: iconHome },
      { to: '/tasks', label: 'Görevler', icon: iconTasks },
      { to: '/calendar', label: 'Takvim', icon: iconCalendar },
    ],
  },
  {
    id: 'management',
    title: t('menu.sections.management'),
    items: [
      { to: '/schema', label: t('menu.items.schema'), icon: iconSchema },
      { to: '/contacts', label: t('menu.items.contacts'), icon: iconContacts },
    ],
  },
  {
    id: 'other',
    title: t('menu.sections.other'),
    items: [
      { to: '/error', label: t('menu.items.errorPages'), icon: iconError },
    ],
  },
]);

const filteredSections = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return sections.value;
  return sections.value
    .map(section => ({
      ...section,
      items: section.items.filter(item => item.label.toLowerCase().includes(q)),
    }))
    .filter(section => section.items.length > 0);
});

const isActive = (path) => {
  if (path === '/') return route.path === '/';
  return route.path.startsWith(path);
};

const toggleSection = (id) => {
  collapsedSections[id] = !collapsedSections[id];
};

const focusSearch = (e) => {
  if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
    e.preventDefault();
    document.querySelector('aside input[type="text"]')?.focus();
  }
};

onMounted(() => window.addEventListener('keydown', focusSearch));
onBeforeUnmount(() => window.removeEventListener('keydown', focusSearch));
</script>

<style scoped>
.scrollbar-thin {
  scrollbar-width: thin;
}
</style>
